<template>
	<div class="package-return-create">
		<ts-page-title :title="$t('packageReturn.pageTitle')" :breadcrumb="[
			{ text: $t('home'), href: '/' },
			{
				text: $t('packageReturn.pageTitle'),
				href: '/admin/agency/package-return'
			},
			{
				text: $t('create'),
				active: true
			}
		]" />

		<ts-loading-banner :loading="loading">
			<div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 lg:tw-gap-6"
				style="min-height: calc(100vh - 200px);">
				<!-- LEFT SIDE: Search & Results -->
				<div class="lg:tw-col-span-2 tw-space-y-4 tw-flex tw-flex-col">
					<!-- Search Section -->
					<section
						class="tw-bg-white dark:tw-bg-[#15202b] tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 dark:tw-border-gray-800 tw-overflow-hidden">
						<div
							class="tw-bg-gradient-to-r tw-from-blue-50 tw-to-blue-100 dark:tw-from-blue-900/20 dark:tw-to-blue-800/20 tw-px-4 tw-py-3 tw-border-b tw-border-blue-200 dark:tw-border-blue-800">
							<div class="tw-flex tw-items-center tw-gap-2.5">
								<div
									class="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-blue-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-shadow">
									<i class="fa fa-search tw-text-sm"></i>
								</div>
								<div>
									<h2 class="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white tw-m-0">
										{{ $t("packageReturn.findPackages") }}
									</h2>
									<p class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400 tw-m-0">
										{{ $t("packageReturn.searchDescription") }}
									</p>
								</div>
							</div>
						</div>

						<div class="tw-p-4">
							<div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-4">
								<!-- Shop Name -->
								<div class="search-field">
									<label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
										<i class="fa fa-store tw-text-blue-500 tw-mr-2"></i>
										{{ $t("packageReturn.shopName") }}
									</label>
									<a-select show-search v-model="filters.shop_id" style="width: 100%"
										:default-active-first-option="false" :show-arrow="true" :filter-option="false"
										:not-found-content="null" :placeholder="$t('packageReturn.selectShop')" @search="handleShopSearch"
										@change="onFilterChange" allowClear>
										<a-select-option v-for="shop in shopList" :key="shop.shop_id" :value="shop.shop_id">
											{{ shop.shop_name }}
										</a-select-option>
									</a-select>
								</div>

								<!-- Driver Name -->
								<div class="search-field">
									<label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
										<i class="fa fa-user-circle tw-text-blue-500 tw-mr-2"></i>
										{{ $t("packageReturn.driverName") }}
									</label>
									<a-select show-search v-model="filters.driver_id" style="width: 100%"
										:default-active-first-option="false" :show-arrow="true" :filter-option="false"
										:not-found-content="null" :placeholder="$t('packageReturn.selectDriver')"
										@search="handleDriverSearch" @change="onFilterChange" allowClear>
										<a-select-option v-for="driver in driverList" :key="driver.driver_id" :value="driver.driver_id">
											{{ driver.driver_name }}
										</a-select-option>
									</a-select>
								</div>

								<!-- Receiver Phone -->
								<div class="search-field">
									<label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
										<i class="fa fa-phone tw-text-blue-500 tw-mr-2"></i>
										{{ $t("packageReturn.receiverPhone") }}
									</label>
									<a-input v-model="filters.receiver_phone" :placeholder="$t('packageReturn.enterReceiverPhone')"
										@pressEnter="searchPackages">
										<a-icon slot="prefix" type="phone" />
									</a-input>
								</div>

								<!-- Booking -->
								<div class="search-field">
									<label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
										<i class="fas fa-list-ol tw-text-blue-500 tw-mr-2"></i>
										{{ $t("packageReturn.bookingNumber") }}
									</label>
									<a-input v-model="filters.booking_number" :placeholder="$t('packageReturn.enterBookingNumber')"
										@pressEnter="searchPackages">
									</a-input>
								</div>
							</div>

							<!-- Action Buttons -->
							<div class="tw-flex tw-gap-3">
								<ts-button color="primary" @click.prevent="searchPackages" class="tw-flex-1 md:tw-flex-initial">
									<i class="fa fa-search tw-mr-2"></i>
									{{ $t("packageReturn.searchPackages") }}
								</ts-button>
								<ts-button color="secondary" @click.prevent="clearFilters">
									<i class="fa fa-redo tw-mr-2"></i>
									{{ $t("packageReturn.reset") }}
								</ts-button>
							</div>
						</div>
					</section>

					<!-- Search Results -->
					<section
						class="tw-bg-white dark:tw-bg-[#15202b] tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 dark:tw-border-gray-800 tw-overflow-hidden tw-flex tw-flex-col tw-flex-1">
						<div
							class="tw-bg-gradient-to-r tw-from-indigo-50 tw-to-indigo-100 dark:tw-from-indigo-900/20 dark:tw-to-indigo-800/20 tw-px-4 tw-py-3 tw-border-b tw-border-indigo-200 dark:tw-border-indigo-800">
							<div class="tw-flex tw-items-center tw-justify-between">
								<div class="tw-flex tw-items-center tw-gap-2.5">
									<div
										class="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-indigo-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-shadow">
										<i class="fa fa-box tw-text-sm"></i>
									</div>
									<div>
										<h2 class="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white tw-m-0">
											{{ $t("packageReturn.searchResults") }}
										</h2>
										<p class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400 tw-m-0">
											<span v-if="packageList.length > 0">
												{{ packageList.length }} {{ $t("packageReturn.packagesFound") }}
											</span>
											<span v-else>
												{{ $t("packageReturn.searchToSeeResults") }}
											</span>
										</p>
									</div>
								</div>

								<!-- Select All -->
								<div v-if="packageList.length > 0"
									class="tw-flex tw-items-center tw-gap-2 tw-bg-white dark:tw-bg-gray-800 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700">
									<a-checkbox :indeterminate="indeterminateSearch" :checked="checkAllSearch"
										@change="onCheckAllSearchChange" />
									<span class="tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
										{{ $t("packageReturn.selectAll") }}
									</span>
								</div>
							</div>
						</div>

						<!-- Empty State - Before Search -->
						<div v-if="!searchPerformed && packageList.length === 0"
							class="tw-p-12 tw-text-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1">
							<div
								class="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-rounded-full tw-bg-indigo-100 dark:tw-bg-indigo-900/20 tw-mb-4">
								<i class="fa fa-search tw-text-4xl tw-text-indigo-400"></i>
							</div>
							<h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-mb-2">
								{{ $t("packageReturn.readyToSearch") }}
							</h3>
							<p class="tw-text-gray-600 dark:tw-text-gray-400">
								{{ $t("packageReturn.useFiltersAbove") }}
							</p>
						</div>

						<!-- Empty State - No Results -->
						<div v-else-if="searchPerformed && packageList.length === 0"
							class="tw-p-12 tw-text-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1">
							<div
								class="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-rounded-full tw-bg-gray-100 dark:tw-bg-gray-800 tw-mb-4">
								<i class="fa fa-inbox tw-text-4xl tw-text-gray-400"></i>
							</div>
							<h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-mb-2">
								{{ $t("packageReturn.noPackagesFound") }}
							</h3>
							<p class="tw-text-gray-600 dark:tw-text-gray-400 tw-mb-4">
								{{ $t("packageReturn.tryDifferentSearch") }}
							</p>
						</div>

						<!-- Package Cards -->
						<div v-else class="tw-p-4 tw-flex-1 tw-overflow-hidden">
							<div class="tw-grid tw-grid-cols-1 tw-gap-2.5 tw-h-full" style="overflow-y: auto;">
								<div v-for="pkg in packageList" :key="pkg.delivery_id"
									class="package-card tw-border tw-rounded-lg tw-p-2.5 tw-transition-all tw-duration-200 tw-cursor-pointer tw-max-h-40"
									:class="{
										'tw-border-blue-500 tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-shadow-md': isSelected(pkg.delivery_id),
										'tw-border-gray-200 dark:tw-border-gray-700 hover:tw-border-gray-300 dark:hover:tw-border-gray-600 hover:tw-shadow': !isSelected(pkg.delivery_id)
									}" @click="togglePackage(pkg)">
									<div class="tw-flex tw-items-start tw-gap-2.5">
										<!-- Checkbox -->
										<div class="tw-pt-0.5" @click.stop>
											<a-checkbox :checked="isSelected(pkg.delivery_id)" @change="togglePackage(pkg)"
												class="tw-scale-110 tw-cursor-pointer" />
										</div>

										<!-- Package Info -->
										<div class="tw-flex-1 tw-min-w-0">
											<div class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mb-2">
												<!-- Left: Booking & Shop -->
												<div class="tw-flex-1">
													<div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
														<span
															class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-blue-100 dark:tw-bg-blue-900 tw-text-blue-700 dark:tw-text-blue-300 tw-text-xs tw-font-bold">
															<i class="fa fa-barcode tw-mr-1 tw-text-xs"></i>
															{{ pkg.delivery_number }}
														</span>
													</div>
													<div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-5 tw-gap-2 tw-text-xs">
														<div class="detail-item">
															<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
																<i class="fa fa-store tw-text-gray-400 tw-text-xs"></i>
																{{ $t("packageReturn.shopName") }}
															</div>
															<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
																{{ pkg.shop_name }}
															</div>
														</div>
														<div class="detail-item">
															<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
																<i class="far fa-clock tw-text-gray-400 tw-text-xs"></i>
																{{ $t("packageReturn.transactionDate") }}
															</div>
															<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
																{{ pkg.transaction_date }}
															</div>
														</div>
														<div class="detail-item">
															<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
																<i class="far fa-clock tw-text-gray-400 tw-text-xs"></i>
																{{ $t("packageReturn.bookingDate") }}
															</div>
															<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
																{{ pkg.booking_time }}
															</div>
														</div>
														<div>
															<div class="tw-text-left">
																<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
																	{{ $t("packageReturn.amount") }}
																</div>
																<div class="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white tw-w-16">
																	<ts-accounting-format :digit="2" :value="pkg.purchase_amount" />
																</div>
															</div>
														</div>
													</div>
												</div>


											</div>

											<!-- Details Grid -->
											<div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-5 tw-gap-2 tw-text-xs">
												<div class="detail-item">
													<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
														<i class="fa fa-user-circle tw-mr-1"></i>
														{{ $t("packageReturn.driver") }}
													</div>
													<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
														{{ pkg.driver_name || "-" }}
													</div>
												</div>

												<div class="detail-item">
													<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
														<i class="fa fa-phone tw-mr-1"></i>
														{{ $t("packageReturn.phone") }}
													</div>
													<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
														{{ pkg.receiver_phone }}
													</div>
												</div>

												<div class="detail-item">
													<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
														<i class="fa fa-cube tw-mr-1"></i>
														{{ $t("packageReturn.type") }}
													</div>
													<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
														{{ pkg.package_type_kh }}
													</div>
												</div>

												<div class="detail-item">
													<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
														<i class="fas fa-toggle-off"></i>
														{{ $t("packageReturn.status") }}
													</div>
													<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
														{{ pkg.delivery_status }}
													</div>
												</div>
												<div class="detail-item">
													<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mb-0.5">
														<i class="fa fa-boxes tw-mr-1"></i>
														{{ $t("packageReturn.qty") }}
													</div>
													<div class="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-xs">
														{{ pkg.number_of_package }}
													</div>
												</div>
											</div>

											<!-- Destination -->
											<div v-if="pkg.destination"
												class="tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
												<div class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
													<i class="fa fa-map-marker-alt tw-text-gray-400 tw-text-xs"></i>
													<span class="tw-text-gray-700 dark:tw-text-gray-300">
														{{ pkg.destination }}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<!-- RIGHT SIDE: Selected Packages & Submit -->
				<div class="lg:tw-col-span-1">
					<div class="tw-sticky tw-top-6">
						<!-- Selected Packages Section -->
						<section
							class="tw-bg-white dark:tw-bg-[#15202b] tw-rounded-xl dark:tw-border-gray-800 tw-overflow-hidden tw-flex tw-flex-col"
							style="height: calc(100vh - 160px);">
							<div
								class="tw-bg-gradient-to-r tw-from-green-50 tw-to-green-100 dark:tw-from-green-900/20 dark:tw-to-green-800/20 tw-px-4 tw-py-3 tw-border-b tw-border-green-200 dark:tw-border-green-800">
								<div class="tw-flex tw-items-center tw-justify-between">
									<div class="tw-flex tw-items-center tw-gap-2.5">
										<div
											class="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-green-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-shadow">
											<i class="fa fa-check-circle tw-text-sm"></i>
										</div>
										<div>
											<h2 class="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white tw-m-0">
												{{ $t("packageReturn.selectedPackages") }}
											</h2>
											<p class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400 tw-m-0">
												{{ selectedPackages.length }} {{ $t("packageReturn.selected") }}
											</p>
										</div>
									</div>
								</div>
							</div>

							<div class="tw-p-4 tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden">
								<!-- Summary Stats -->
								<div class="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-3 tw-flex-shrink-0">
									<div
										class="tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-border tw-border-blue-200 dark:tw-border-blue-800 tw-rounded-lg tw-p-2">
										<div class="tw-text-xs tw-text-blue-600 dark:tw-text-blue-400 tw-font-medium tw-mb-0.5">
											{{ $t("packageReturn.totalPackages") }}
										</div>
										<div class="tw-text-lg tw-font-bold tw-text-blue-700 dark:tw-text-blue-300">
											{{ totalPackages }}
										</div>
									</div>

									<div
										class="tw-bg-purple-50 dark:tw-bg-purple-900/20 tw-border tw-border-purple-200 dark:tw-border-purple-800 tw-rounded-lg tw-p-2">
										<div class="tw-text-xs tw-text-purple-600 dark:tw-text-purple-400 tw-font-medium tw-mb-0.5">
											{{ $t("packageReturn.totalAmount") }}
										</div>
										<div class="tw-text-lg tw-font-bold tw-text-purple-700 dark:tw-text-purple-300">
											<ts-accounting-format :digit="0" :value="totalAmount" />
										</div>
									</div>
								</div>

								<!-- Selected Items List -->
								<div class="tw-mb-3 tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden">
									<div class="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-flex-shrink-0">
										<h3
											class="tw-text-xs tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-uppercase tw-tracking-wide tw-m-0">
											{{ $t("packageReturn.items") }}
										</h3>
										<button @click.prevent="clearSelection" v-if="selectedPackages.length > 0"
											class="tw-text-xs tw-text-red-600 hover:tw-text-red-700 tw-font-medium">
											<i class="fa fa-times-circle tw-mr-1"></i>
											{{ $t("packageReturn.clearAll") }}
										</button>
									</div>

									<!-- Empty State -->
									<div v-if="selectedPackages.length === 0"
										class="tw-text-center tw-py-8 tw-border-2 tw-border-dashed tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1">
										<div
											class="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 dark:tw-bg-gray-800 tw-mb-3">
											<i class="fa fa-box-open tw-text-3xl tw-text-gray-400"></i>
										</div>
										<p class="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
											{{ $t("packageReturn.noSelection") }}
										</p>
									</div>

									<!-- Selected Items -->
									<div v-else class="tw-space-y-1.5 tw-flex-1 tw-overflow-y-auto">
										<div v-for="(pkg, index) in getSelectedPackageDetails()" :key="pkg.delivery_id"
											class="tw-group tw-relative tw-p-2 tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100/50 dark:tw-from-gray-800 dark:tw-to-gray-800/50 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 hover:tw-border-red-400 dark:hover:tw-border-red-600 hover:tw-shadow-sm tw-transition-all">
											<div class="tw-flex tw-items-start tw-gap-2">
												<!-- Icon Badge -->
												<div
													class="tw-w-7 tw-h-7 tw-rounded-md tw-bg-gradient-to-br tw-from-blue-500 tw-to-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm">
													<!-- <i class="fa fa-box tw-text-white tw-text-xs"></i> -->
													<span class="tw-text-white tw-text-lg">{{ index + 1 }}</span>
												</div>

												<!-- Content -->
												<div class="tw-flex-1 tw-min-w-0">
													<!-- Booking Number -->
													<div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
														<span class="tw-text-xs tw-font-bold tw-text-gray-900 dark:tw-text-white tw-truncate">
															{{ pkg.delivery_number }}
														</span>
														<button @click.prevent="removePackage(pkg.delivery_id)"
															class="tw-w-5 tw-h-5 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-white hover:tw-bg-red-500 dark:hover:tw-bg-red-600 tw-transition-all tw-flex-shrink-0">
															<i class="fa fa-times tw-text-xs"></i>
														</button>
													</div>

													<!-- Shop Name -->
													<div class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400 tw-truncate tw-mb-1">
														{{ pkg.shop_name }} - {{ pkg.receiver_phone }}
													</div>

													<!-- Receiver Phone -->
													<!-- <div class="tw-flex tw-items-center tw-gap-1 tw-mb-1.5">
														<i class="fa fa-phone tw-text-xs tw-text-gray-400"></i>
														<span class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400"></span>
													</div> -->

													<!-- Amount & Quantity -->
													<div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
														<div class="tw-flex tw-items-baseline tw-gap-0.5">
															<span class="tw-text-xs tw-font-bold tw-text-blue-600 dark:tw-text-blue-400">$</span>
															<span class="tw-text-xs tw-font-bold tw-text-blue-600 dark:tw-text-blue-400">{{
																pkg.purchase_amount.toFixed(2) }}</span>
														</div>
														<div
															class="tw-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded">
															<i class="fa fa-boxes tw-text-xs tw-text-gray-500 dark:tw-text-gray-400"></i>
															<span class="tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">{{
																pkg.number_of_package }}</span>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<!-- Assign Driver for Return -->
								<div class="tw-mb-3 tw-flex-shrink-0">
									<label
										class="tw-block tw-text-xs tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-1.5 required">
										<i class="fa fa-user-circle tw-text-blue-500 tw-mr-1.5"></i>
										{{ $t("packageReturn.assignDriverLabel") }}
									</label>
									<a-select show-search v-model="assignedDriverId" style="width: 100%"
										:default-active-first-option="false" :show-arrow="true" :filter-option="false"
										:not-found-content="null" :placeholder="$t('packageReturn.selectDriverForReturn')"
										@search="handleAssignDriverSearch" allowClear>
										<a-select-option v-for="driver in assignDriverList" :key="driver.driver_id"
											:value="driver.driver_id">
											{{ driver.driver_name }}
										</a-select-option>
									</a-select>
									<div v-if="errors.has('driver_id')" class="tw-text-red-500 tw-text-xs tw-font-medium tw-mt-1.5">
										<i class="fa fa-exclamation-circle tw-mr-1"></i>
										{{ errors.first("driver_id") }}
									</div>
								</div>

								<!-- Return Reason -->
								<div class="tw-mb-3 tw-flex-shrink-0">
									<label class="tw-block tw-text-xs tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-1.5">
										<i class="fa fa-comment-dots tw-text-orange-500 tw-mr-1.5"></i>
										{{ $t("packageReturn.reasonLabel") }}
									</label>
									<a-textarea v-model="returnReason" :placeholder="$t('packageReturn.reasonPlaceholder')" :rows="4"
										:maxLength="500" :auto-size="{ minRows: 2, maxRows: 6 }" class="tw-w-full tw-text-xs" />
									<div class="tw-flex tw-justify-between tw-mt-1.5">
										<div v-if="errors.has('reason')" class="tw-text-red-500 tw-text-xs tw-font-medium">
											<i class="fa fa-exclamation-circle tw-mr-1"></i>
											{{ errors.first("reason") }}
										</div>
										<div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-ml-auto">
											{{ returnReason.length }}/500
										</div>
									</div>
								</div>

								<!-- Submit Actions -->
								<div class="tw-flex tw-flex-col tw-gap-2 tw-flex-shrink-0">
									<ts-button color="primary" @click.prevent="onSubmit"
										:disabled="selectedPackages.length === 0 || !assignedDriverId" class="tw-w-full">
										<i class="fa fa-paper-plane tw-mr-2"></i>
										{{ $t("packageReturn.submitReturn") }}
									</ts-button>
									<ts-button color="secondary" @click.prevent="onCancel" class="tw-w-full">
										<i class="fa fa-arrow-left tw-mr-2"></i>
										{{ $t("cancel") }}
									</ts-button>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</ts-loading-banner>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";
import { Errors } from "form-backend-validation";

export default {
	name: "packageReturnCreate",
	data() {
		return {
			loading: false,
			errors: new Errors(),
			filters: {
				shop_id: undefined,
				driver_id: undefined,
				receiver_phone: "",
				booking_number: ""
			},
			shopList: [],
			driverList: [],
			assignDriverList: [],
			packageList: [],
			selectedPackages: [],
			selectedPackageData: [],
			assignedDriverId: undefined,
			returnReason: "",
			checkAllSearch: false,
			indeterminateSearch: false,
			searchPerformed: false
		};
	},
	computed: {
		hasAnyFilter() {
			return (
				this.filters.shop_id ||
				this.filters.driver_id ||
				this.filters.receiver_phone.trim()
			);
		},
		totalPackages() {
			return this.selectedPackageData.reduce(
				(sum, pkg) => sum + (pkg.number_of_package || 0),
				0
			);
		},
		totalAmount() {
			return this.selectedPackageData.reduce(
				(sum, pkg) => sum + (pkg.purchase_amount || 0),
				0
			);
		}
	},
	methods: {
		...mapActions("agency/packageReturn", {
			searchPackagesAction: "searchPackages",
			submitReturn: "submitReturn",
			getFormViewData: "getFormViewData"
		}),

		handleShopSearch: debounce(async function (value) {
			if (!value) return;
			this.loading = true;
			try {
				const response = await this.getFormViewData({
					index: "shop",
					params: {
						fnName: "shop",
						search_shop: value
					}
				});
				this.shopList = response.data["shop"] || [];
			} catch (error) {
				console.error("Shop search error:", error);
			} finally {
				this.loading = false;
			}
		}, 500),

		handleDriverSearch: debounce(async function (value) {
			if (!value) return;
			this.loading = true;
			try {
				const response = await this.getFormViewData({
					index: "driver",
					params: {
						fnName: "driver",
						search_driver: value
					}
				});
				this.driverList = response.data["driver"] || [];
			} catch (error) {
				console.error("Driver search error:", error);
			} finally {
				this.loading = false;
			}
		}, 500),

		handleAssignDriverSearch: debounce(async function (value) {
			if (!value) return;
			this.loading = true;
			try {
				const response = await this.getFormViewData({
					index: "driver",
					params: {
						fnName: "driver",
						search_driver: value
					}
				});
				this.assignDriverList = response.data["driver"] || [];
			} catch (error) {
				console.error("Assign driver search error:", error);
			} finally {
				this.loading = false;
			}
		}, 500),

		onFilterChange() {
			// Optional: Auto-search on filter change
		},

		async searchPackages() {
			this.loading = true;
			this.searchPerformed = true;
			try {
				const response = await this.searchPackagesAction(this.filters);
				this.packageList = response.data || [];

				if (this.packageList.length === 0) {
					this.$notify({
						type: "info",
						text: this.$t("packageReturn.noPackagesFound")
					});
				}

				this.updateCheckAllSearchStatus();
			} catch (error) {
				this.$notify({
					type: "error",
					text: error.message || this.$t("packageReturn.searchError")
				});
			} finally {
				this.loading = false;
			}
		},

		clearFilters() {
			this.filters = {
				shop_id: undefined,
				driver_id: undefined,
				receiver_phone: "",
				booking_number: ""
			};
			this.packageList = [];
			this.searchPerformed = false;
			this.checkAllSearch = false;
			this.indeterminateSearch = false;
		},

		isSelected(deliveryId) {
			return this.selectedPackages.includes(deliveryId);
		},

		togglePackage(pkg) {
			const index = this.selectedPackages.indexOf(pkg.delivery_id);
			if (index > -1) {
				this.selectedPackages.splice(index, 1);
				const dataIndex = this.selectedPackageData.findIndex(
					p => p.delivery_id === pkg.delivery_id
				);
				if (dataIndex > -1) {
					this.selectedPackageData.splice(dataIndex, 1);
				}
			} else {
				this.selectedPackages.push(pkg.delivery_id);
				this.selectedPackageData.push(pkg);
			}
			this.updateCheckAllSearchStatus();
		},

		onCheckAllSearchChange(e) {
			if (e.target.checked) {
				this.packageList.forEach(pkg => {
					if (!this.isSelected(pkg.delivery_id)) {
						this.selectedPackages.push(pkg.delivery_id);
						this.selectedPackageData.push(pkg);
					}
				});
			} else {
				this.packageList.forEach(pkg => {
					const index = this.selectedPackages.indexOf(pkg.delivery_id);
					if (index > -1) {
						this.selectedPackages.splice(index, 1);
						const dataIndex = this.selectedPackageData.findIndex(
							p => p.delivery_id === pkg.delivery_id
						);
						if (dataIndex > -1) {
							this.selectedPackageData.splice(dataIndex, 1);
						}
					}
				});
			}
			this.updateCheckAllSearchStatus();
		},

		updateCheckAllSearchStatus() {
			const selectedInSearchResults = this.packageList.filter(pkg =>
				this.isSelected(pkg.delivery_id)
			).length;

			this.indeterminateSearch =
				selectedInSearchResults > 0 &&
				selectedInSearchResults < this.packageList.length;
			this.checkAllSearch =
				selectedInSearchResults === this.packageList.length &&
				this.packageList.length > 0;
		},

		clearSelection() {
			this.selectedPackages = [];
			this.selectedPackageData = [];
			this.assignedDriverId = undefined;
			this.returnReason = "";
			this.checkAllSearch = false;
			this.indeterminateSearch = false;
		},

		removePackage(deliveryId) {
			const index = this.selectedPackages.indexOf(deliveryId);
			if (index > -1) {
				this.selectedPackages.splice(index, 1);
			}
			const dataIndex = this.selectedPackageData.findIndex(
				p => p.delivery_id === deliveryId
			);
			if (dataIndex > -1) {
				this.selectedPackageData.splice(dataIndex, 1);
			}
			this.updateCheckAllSearchStatus();
		},

		getSelectedPackageDetails() {
			return this.selectedPackageData;
		},

		async onSubmit() {
			if (this.selectedPackages.length === 0) {
				this.$notify({
					type: "warning",
					text: this.$t("packageReturn.selectPackagesRequired")
				});
				return;
			}

			if (!this.assignedDriverId) {
				this.$notify({
					type: "warning",
					text: this.$t("packageReturn.driverRequired")
				});
				return;
			}

			this.loading = true;
			this.errors = new Errors();

			try {
				const response = await this.submitReturn({
					delivery_ids: this.selectedPackages,
					driver_id: this.assignedDriverId,
					reason: this.returnReason
				});

				this.$notify({
					type: "success",
					text:
						response.message ||
						this.$t("packageReturn.returnSuccess")
				});

				// Reset form
				this.clearFilters();
				this.clearSelection();

				// Navigate back to list
				this.$router.push("/admin/agency/package-return");
			} catch (error) {
				this.errors = new Errors(error.errors || {});
				this.$notify({
					type: "error",
					text: error.message || this.$t("packageReturn.returnError")
				});
			} finally {
				this.loading = false;
			}
		},

		onCancel() {
			this.$router.push("/admin/agency/package-return");
		},

		async fetchInitialData() {
			this.loading = true;
			try {
				const response = await this.getFormViewData({
					index: "shop,driver",
					params: {
						fnName: "shop,driver"
					}
				});
				this.shopList = response.data["shop"] || [];
				this.driverList = response.data["driver"] || [];
				this.assignDriverList = response.data["driver"] || [];
			} catch (error) {
				console.error("Failed to fetch initial data:", error);
			} finally {
				this.loading = false;
			}
		}
	},
	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.fetchInitialData();
		});
	}
};
</script>